import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface SoftVoidConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void> | void;
  recordIdentifier: string;
  title?: string;
  description?: string;
}

export const SoftVoidConfirmModal: React.FC<SoftVoidConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  recordIdentifier,
  title = 'Confirm Record Voiding',
  description = 'This transaction will be excluded from settlement calculations, but will remain permanently visible in the audit trail.',
}) => {
  const [voidReason, setVoidReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!voidReason.trim()) {
      setError('A mandatory void reason is required.');
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      await onConfirm(voidReason.trim());
      setVoidReason('');
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to void record');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="w-5 h-5" />
            <DialogTitle>{title}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <p className="text-sm text-slate-700">
            You are about to soft-void record: <strong className="text-slate-900 font-mono">{recordIdentifier}</strong>.
          </p>
          <p className="text-xs text-slate-500 bg-amber-50 p-2.5 rounded-md border border-amber-200">
            {description}
          </p>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Mandatory Void Reason: <span className="text-red-500">*</span>
            </label>
            <textarea
              value={voidReason}
              onChange={(e) => {
                setVoidReason(e.target.value);
                if (error) setError(null);
              }}
              rows={3}
              placeholder="e.g. Duplicate invoice entry, scale calibration error, operator typo..."
              className="w-full text-sm rounded-md border border-slate-300 p-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600"
            />
            {error && <p className="text-xs text-red-600 mt-1 font-medium">{error}</p>}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel / Keep Record
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={isSubmitting || !voidReason.trim()}>
            {isSubmitting ? 'Voiding...' : '❌ Confirm Soft-Void'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
